import clsx from "clsx";
import { useRef } from "react";

import { loadLocale } from "@smart/itops-locale-dom";
import { TextFieldInput } from "@smart/itops-sb-design-system-dom";

import { getFieldSetClassNames, hiddenInputStyles } from "./class-names";
import { FieldGroup } from "./field-group";
import { Label } from "./label";
import { FieldProps, FieldWrapper } from "./wrapper";
import { fieldName } from "../../hooks";

export const DateFieldInput = ({
  field,
  index,
  innerRef,
  value,
  error,
  onChange,
  onBlur,
}: Omit<FieldProps<HTMLInputElement>, "disabled">) => {
  const locale = loadLocale();
  const dayRef = useRef<HTMLInputElement>(null);
  const monthRef = useRef<HTMLInputElement>(null);
  const yearRef = useRef<HTMLInputElement>(null);

  const [yearValue, monthValue, dayValue] = `${value ?? ""}`
    .replace(/[^\d-]/g, "")
    .split("-");

  const parts = {
    day: {
      label: "Day",
      placeholder: "DD",
      ref: dayRef,
      value: dayValue,
      minLength: 1,
      maxLength: 2,
      index: 2,
    },
    month: {
      label: "Month",
      placeholder: "MM",
      ref: monthRef,
      value: monthValue,
      minLength: 1,
      maxLength: 2,
      index: 1,
    },
    year: {
      label: "Year",
      placeholder: "YYYY",
      ref: yearRef,
      value: yearValue,
      minLength: 4,
      maxLength: 4,
      index: 0,
    },
  };

  const onChangePart = (part: keyof typeof parts, v: string) => {
    const updated = [yearValue, monthValue, dayValue];
    updated[parts[part].index] = v.replace(/[^\d+]/g, "");

    const updatedValue = updated.map((p) => p || "").join("-");
    onChange(updatedValue === "--" ? "" : updatedValue);
  };
  const id = fieldName({ field, index });
  const errorId = fieldName({ field, index, suffix: "error" });

  return (
    <FieldGroup
      id={id}
      hint={field.hint ?? undefined}
      message={error}
      messageId={error ? errorId : undefined}
      label={<Label {...field} />}
    >
      <fieldset
        className={clsx(getFieldSetClassNames({ dir: "row" }), "max-w-[18rem]")}
        aria-invalid={!!error}
        aria-errormessage={error ? errorId : undefined}
        data-testid="date-field-input"
      >
        <input
          ref={innerRef}
          id={id}
          name={id}
          value={value ?? ""}
          className={hiddenInputStyles}
          aria-invalid={!!error}
          aria-errormessage={error ? errorId : undefined}
          readOnly
          onFocus={() => {
            const part =
              locale.dateFormat.find((p) => !parts[p].value) ||
              locale.dateFormat[2];

            parts[part].ref.current?.focus();
          }}
        />
        {locale.dateFormat.map((part, idx) => (
          <TextFieldInput
            key={part}
            ref={parts[part].ref}
            id={fieldName({ field, index, suffix: part })}
            name={fieldName({ field, index, suffix: part })}
            aria-invalid={!!error}
            aria-errormessage={error ? errorId : undefined}
            aria-label={parts[part].label}
            error={!!error}
            value={parts[part].value ?? ""}
            minLength={parts[part].minLength}
            maxLength={parts[part].maxLength}
            onBlur={onBlur}
            onChange={(e) => {
              onChangePart(part, e.currentTarget.value);
            }}
            inputMode="numeric"
            pattern="\d+"
            placeholder={parts[part].placeholder}
            className={parts[part].minLength > 2 ? "w-32" : "w-20"}
            onKeyDown={(e) => {
              const prevPart = parts[locale.dateFormat[idx - 1]]?.ref.current;
              const nextPart = parts[locale.dateFormat[idx + 1]]?.ref.current;

              const atStart =
                e.currentTarget.selectionStart === 0 &&
                e.currentTarget.selectionEnd === 0;
              const atEnd =
                e.currentTarget.selectionStart &&
                e.currentTarget.selectionStart >= e.currentTarget.value.length;

              const goBack = () => {
                prevPart?.focus();
                prevPart?.setSelectionRange(
                  value.length,
                  value.length,
                  "backward",
                );
              };
              const goNext = () => {
                nextPart?.focus();
                nextPart?.setSelectionRange(0, 0, "forward");
              };

              if (e.key === "Backspace" && e.currentTarget.value === "") {
                e.preventDefault();
                goBack();
              }
              if (
                e.key.match(/^\d$/) &&
                atEnd &&
                e.currentTarget.value.length >= parts[part].maxLength
              ) {
                goNext();
              }
              if (e.key === "/") {
                e.preventDefault();
                if (atEnd) {
                  goNext();
                }
              }
              if (e.key === "ArrowLeft") {
                if (atStart) {
                  e.preventDefault();
                  goBack();
                }
              }
              if (e.key === "ArrowRight") {
                if (atEnd) {
                  e.preventDefault();
                  goNext();
                }
              }
            }}
          />
        ))}
      </fieldset>
    </FieldGroup>
  );
};

export const DateField = (props: FieldProps<HTMLInputElement>) => {
  const { loading, disabled } = props;
  return (
    <FieldWrapper disabled={disabled} isLoading={loading}>
      <DateFieldInput {...props} />
    </FieldWrapper>
  );
};
